import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getBatch } from './getBatch';
import { getASBatch } from './getASBatch';  // Make sure this import is present

const ListContext = createContext(null);

export function ListProvider({ 
  children, 
  initialItems = [], 
  query = {}, 
  projection = {}, 
  sort = {}, 
  batchSize = 20, 
  autoLoadThreshold = 30,
  loadAllAtOnce = true
}) {
  const [items, setItems] = useState(initialItems);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const initialLoadRef = useRef(true);
  const loadedAllRef = useRef(false);
  const prevQueryRef = useRef({});
  
  // Add debug logging to see what the query looks like
  useEffect(() => {
    console.log('ListProvider received query:', query);
  }, [query]);
  
  // Check if this is an Atlas Search pipeline (array with $search)
  const isAtlasSearch = Array.isArray(query) && 
                        query.length > 0 && 
                        query[0].$search !== undefined;
  
  // Also check for non-array $search query (just to be safe)
  const isObjectAtlasSearch = !Array.isArray(query) && 
                              query && 
                              query.$search !== undefined;
  
  // Log detection results for debugging
  useEffect(() => {
    console.log('Query type detection:', { 
      isArrayPipeline: Array.isArray(query),
      isAtlasSearch,
      isObjectAtlasSearch
    });
  }, [query, isAtlasSearch, isObjectAtlasSearch]);

  const fetchMoreItems = useCallback(async () => {
    if (loading || !hasMore || (loadAllAtOnce && loadedAllRef.current)) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching data with:', { 
        query, 
        page, 
        batchSize, 
        projection, 
        sort, 
        loadAllAtOnce,
        isAtlasSearch,
        isObjectAtlasSearch
      });
      
      let newItems = [];
      
      // Handle both array and object Atlas Search queries
      if (isAtlasSearch || isObjectAtlasSearch) {
        // For Atlas Search, we always get all results at once
        if (page === 1) {
          console.log('Using Atlas Search - calling getASBatch');
          
          if (isAtlasSearch) {
            // Handle pipeline array
            const pipeline = [...query];
            
            // Add projection and sort if they aren't already in the pipeline
            if (Object.keys(projection).length > 0 && 
                !pipeline.some(stage => Object.keys(stage)[0] === '$project')) {
              pipeline.push({ $project: projection });
            }
            
            if (Object.keys(sort).length > 0 && 
                !pipeline.some(stage => Object.keys(stage)[0] === '$sort')) {
              pipeline.push({ $sort: sort });
            }
            
            console.log('Calling getASBatch with pipeline:', pipeline);
            newItems = await getASBatch(pipeline, 1000);
          } else {
            // Handle object $search query
            const pipeline = [{ $search: query.$search }];
            
            // Add projection and sort
            if (Object.keys(projection).length > 0) {
              pipeline.push({ $project: projection });
            }
            
            if (Object.keys(sort).length > 0) {
              pipeline.push({ $sort: sort });
            }
            
            console.log('Calling getASBatch with pipeline:', pipeline);
            newItems = await getASBatch(pipeline, 1000);
          }
          
          setHasMore(false);
          loadedAllRef.current = true;
        } else {
          // We've already loaded all Atlas Search results on page 1
          newItems = [];
        }
      } else {
        // For regular queries, use getBatch
        console.log('Using regular query - calling getBatch');
        const effectiveBatchSize = loadAllAtOnce && page === 1 ? 1000 : batchSize;
        newItems = await getBatch(query, page, effectiveBatchSize, projection, sort);
        
        if (!newItems || newItems.length < effectiveBatchSize || (loadAllAtOnce && page === 1)) {
          setHasMore(false);
        }
        
        if (loadAllAtOnce && page === 1) {
          loadedAllRef.current = true;
        }
      }
      
      console.log(`Fetch returned ${newItems.length} items`);
      setItems(prev => [...prev, ...(newItems || [])]);
      setPage(prev => prev + 1);
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, query, page, batchSize, projection, sort, loadAllAtOnce, isAtlasSearch, isObjectAtlasSearch]);

  // The rest of the ListProvider code remains unchanged
  const resetList = useCallback(() => {
    setItems(initialItems);
    setLoading(false);
    setHasMore(true);
    setPage(1);
    setError(null);
    initialLoadRef.current = true;
    loadedAllRef.current = false;
  }, [initialItems]);

  // Check if query has changed and reset if needed
  useEffect(() => {
    const queryChanged = JSON.stringify(prevQueryRef.current) !== JSON.stringify(query);
    
    if (queryChanged) {
      console.log('Query changed, resetting list');
      resetList();
      prevQueryRef.current = query;
    }
  }, [query, resetList]);

  // Load initial data when component mounts or when reset
  useEffect(() => {
    if (initialLoadRef.current && !loading) {
      console.log('Initial data load triggered');
      initialLoadRef.current = false;
      fetchMoreItems();
    }
  }, [fetchMoreItems, loading]);

  return (
    <ListContext.Provider value={{ 
      items, 
      loading, 
      hasMore, 
      error,
      fetchMoreItems,
      resetList,
      autoLoadThreshold,
      query,
      isAtlasSearch: isAtlasSearch || isObjectAtlasSearch
    }}>
      {children}
    </ListContext.Provider>
  );
}

export function useList() {
  const context = useContext(ListContext);
  if (!context) {
    throw new Error('useList must be used within a ListProvider');
  }
  return context;
}